import { createSelector } from "@reduxjs/toolkit";
import {
  ExperienceBadge,
  ExperienceBookableItem,
  ExperiencesAgeBandEnum,
  ExperiencesCancellationSummary,
  ExperiencesPriceBreakdown,
  ITrackingProperties,
  SpecificAgeBand,
  ViewedExperienceDetailsProperties,
} from "redmond";

import { IStoreState } from "../../../../reducers/types";
import { IShopOverview } from "../../components/ShopOverview/component";
import { IShopWhatsIncludedProps } from "../../components/ShopWhatsIncluded/component";
import { IShopWhatToExpect } from "../../components/ShopWhatToExpect/component";
import { IShopPhotoGalleryProps } from "../../components/ShopPhotoGallery/component";
import { IShopSimilar } from "../../components/ShopSimilar/component";
import { IShopCancellation } from "../../components/ShopCancellationPolicy/component";
import { IShopCustomerReviews } from "../../components/ShopCustomerReviews/component";
import { IShopContactInformationProps } from "../../../common";
import { IShopCheckoutBreakdown } from "../../components/ShopCheckoutBreakdown/component";
import { getExperiencesEntryProperties } from "../../../availability/reducer";

export const getExperiencesShopCallState = (state: IStoreState) =>
  state.experiencesShop.experiencesShopCallState;

export const getSelectedExperienceId = (state: IStoreState) =>
  state.experiencesShop.selectedExperienceId;

export const getExperiencesShopDetails = (state: IStoreState) =>
  state.experiencesShop.experiencesShopDetails;

export const getTravelerCounts = (state: IStoreState) =>
  state.experiencesShop.travelerCounts;

export const getExperiencesShopReviews = (state: IStoreState) =>
  state.experiencesShop.selectedExperienceReviews;

export const getExperiencesShopPriceBreakdowns = (state: IStoreState) =>
  state.experiencesShop.experiencesPriceBreakdowns;

export const getExperiencesNoAvailability = (state: IStoreState) =>
  state.experiencesShop.experiencesPriceBreakdowns?.length === 0;

export const getSelectedTime = (state: IStoreState) =>
  state.experiencesShop.selectedTime;

export const getExperienceId = createSelector(
  getExperiencesShopDetails,
  (experiencesShopDetails) => {
    if (!experiencesShopDetails) return null;

    return experiencesShopDetails.id;
  }
);

export const getExperienceName = createSelector(
  getExperiencesShopDetails,
  (experiencesShopDetails) => {
    if (!experiencesShopDetails) return null;

    return experiencesShopDetails.name;
  }
);

export const getExperienceProviderName = createSelector(
  getExperiencesShopDetails,
  (experiencesShopDetails) => {
    if (!experiencesShopDetails) return null;

    return experiencesShopDetails.supplierInfo.name;
  }
);

export const getExperiencesSearchLocation = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): string | null => {
    if (!experienceShopDetails) return null;

    return experienceShopDetails.searchLocation;
  }
);

export const getExperiencesShopBookableItems = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): ExperienceBookableItem[] => {
    if (!experienceShopDetails) return [];

    const filteredBookableItems = experienceShopDetails.bookableItems.filter(
      (bookableItem) => Object.entries(bookableItem.timeSlotsByDate).length > 0
    );
    return filteredBookableItems;
  }
);

export const getExperienceMaxTravelerCount = createSelector(
  getExperiencesShopDetails,
  (experiencesShopDetails): number | undefined => {
    if (!experiencesShopDetails) return undefined;

    return experiencesShopDetails.bookingRequirements.maxTravellers;
  }
);

export const getExperiencesShopOverview = createSelector(
  getExperiencesShopDetails,
  getExperiencesShopBookableItems,
  getExperienceMaxTravelerCount,
  getExperiencesShopPriceBreakdowns,
  (
    experienceShopDetails,
    bookableItems,
    maxTravelerCount,
    priceBreakdowns
  ): IShopOverview | null => {
    if (!experienceShopDetails) return null;

    return {
      isSkeleton: false,
      title: experienceShopDetails.name,
      experienceId: experienceShopDetails.id,
      overview: experienceShopDetails.overview,
      rating: experienceShopDetails.rating,
      duration: experienceShopDetails.duration,
      locationLogistics: experienceShopDetails.logistics,
      languageGuides: experienceShopDetails.languageGuides || [],
      provider: experienceShopDetails.supplierInfo.name,
      badges: experienceShopDetails.badges.filter(
        (badge) => badge !== ExperienceBadge.FreeCancellation
      ),
      bookableItems,
      pricingCategory: experienceShopDetails.pricingCategory,
      experienceMaxTravelers: maxTravelerCount,
      adultRequired: experienceShopDetails.bookingRequirements.adultRequired,
      dailyPrices: experienceShopDetails.dailyPrices,
      ticket:
        experienceShopDetails.ticketInfo?.ticketTypes &&
        experienceShopDetails.ticketInfo?.ticketTypes.length > 0
          ? experienceShopDetails.ticketInfo?.ticketTypes[0]
          : undefined,
      timeSelectOptions:
        priceBreakdowns?.map((breakdown) => breakdown.startTime || "") || [],
    };
  }
);

export const getExperiencesShopAgeBands = createSelector(
  getExperiencesShopDetails,
  (experiencesShopDetails) => {
    if (!experiencesShopDetails) return undefined;

    const parsedAgeBands: {
      [key in ExperiencesAgeBandEnum]?: SpecificAgeBand;
    } = {};

    experiencesShopDetails.ageBands.forEach((ageBand) => {
      parsedAgeBands[ageBand.ageBand] = ageBand;
    });

    return parsedAgeBands;
  }
);

export const getExperiencesShopWhatsIncluded = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): IShopWhatsIncludedProps | null => {
    if (!experienceShopDetails) return null;

    return {
      isSkeleton: false,
      additionalInformation: experienceShopDetails.additionalInformation,
      knowBeforeYouBook: experienceShopDetails.knowBeforeYouBook,
      inclusionsAndExclusions: experienceShopDetails.inclusionsAndExclusions,
    };
  }
);

export const getExperiencesShopCancellationPolicy = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): IShopCancellation | null => {
    if (!experienceShopDetails) return null;

    return {
      isSkeleton: false,
      cancellationPolicy: experienceShopDetails.cancellationPolicy,
    };
  }
);

export const getExperiencesShopContactInfo = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): IShopContactInformationProps | null => {
    if (!experienceShopDetails) return null;

    return {
      isSkeleton: false,
      supplierEmail: experienceShopDetails.supplierInfo.email,
      supplierPhone: experienceShopDetails.supplierInfo.phone,
    };
  }
);

export const getExperiencesShopWhatToExpect = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): IShopWhatToExpect | null => {
    if (!experienceShopDetails) return null;

    return {
      isSkeleton: false,
      itinerary: experienceShopDetails.itinerary,
    };
  }
);

export const getExperiencesShopCustomerReviews = createSelector(
  getExperiencesShopReviews,
  getExperiencesShopDetails,
  (customerReviews, experienceShopDetails): IShopCustomerReviews | null => {
    if (!customerReviews && !experienceShopDetails) return null;

    return {
      isSkeleton: false,
      summary: customerReviews?.summary ?? experienceShopDetails?.rating,
      reviews: customerReviews?.reviews ?? [],
    };
  }
);

// export const getExperiencesShopBookableItemsToDisplay = createSelector(
//   getExperiencesShopDetails,
//   (
//     experiencesShopDetails
//   ): {
//     experienceDailyPrice: ExperienceDailyPrice;
//     startTimes: string[];
//   } | null => {
//     if (!experiencesShopDetails) return null;

//     const bookableItems = experiencesShopDetails.bookableItems;
//     const dailyPrices = experiencesShopDetails.dailyPrices;

//     const parsedBookableItems = [];
//     dailyPrices.slice(0, 4).map((dailyPrice) => {
//       bookableItems.map(() => {

//       })
//     })
//     return null;
//   }
// );

export const getExperiencesShopGallery = createSelector(
  getExperiencesShopDetails,
  getExperiencesShopReviews,
  getExperienceMaxTravelerCount,
  (
    experienceShopDetails,
    customerReviews,
    maxTravelerCount
  ): IShopPhotoGalleryProps | null => {
    if (!experienceShopDetails) return null;

    const { coverImage, additionalImages, name, duration, rating, badges } =
      experienceShopDetails;
    const providerPhotos = [coverImage, ...additionalImages];

    const reviewsWithPhotos = (customerReviews?.reviews || []).filter(
      ({ travelerPhotos }) => !!travelerPhotos.length
    );
    const travelerPhotos = reviewsWithPhotos.map(
      (review) => review.travelerPhotos[0]
    );

    return {
      isSkeleton: false,
      providerPhotos,
      travelerPhotos,
      experienceName: name,
      duration,
      rating,
      isFreeCancellation: badges.includes(ExperienceBadge.FreeCancellation),
      experienceMaxTravelers: maxTravelerCount,
    };
  }
);

export const getExperiencesShopSimilar = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): IShopSimilar | null => {
    if (!experienceShopDetails) return null;

    const {
      coverImage,
      additionalImages,
      name,
      overview,
      duration,
      rating,
      badges,
      similarExperiences,
      pricingCategory,
      bestPrice,
      id,
      languageGuides,
      itinerary,
    } = experienceShopDetails;

    return {
      isSkeleton: false,
      selectedExperienceInfo: {
        name,
        rating,
        duration,
        coverImage,
        additionalImages,
        cancellationSummary: badges.includes(ExperienceBadge.FreeCancellation)
          ? ExperiencesCancellationSummary.FreeCancellation
          : ExperiencesCancellationSummary.PaidCancellation,
        pricingCategory,
        bestPrice,
        id,
        languageGuides,
        itinerary,
        description: overview,
        tags: [],
        popularFeatureTags: [],
        badges,
      },
      similarExperiences,
    };
  }
);

export const getExperiencesShopPriceBreakdown = createSelector(
  getExperiencesShopPriceBreakdowns,
  getExperiencesShopDetails,
  getSelectedTime,
  (
    priceBreakdowns,
    experiencesShopDetails,
    selectedTime
  ): ExperiencesPriceBreakdown | undefined => {
    if (!experiencesShopDetails) return undefined;

    if (!priceBreakdowns || priceBreakdowns.length === 0) {
      return experiencesShopDetails.examplePriceBreakdown;
    }

    if (selectedTime) {
      return (
        priceBreakdowns.find(
          (breakdown) => breakdown.startTime === selectedTime
        ) || priceBreakdowns[0]
      );
    }

    return priceBreakdowns[0];
  }
);

export const getIsFreeCancellation = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): boolean => {
    if (!experienceShopDetails) return false;

    return (
      experienceShopDetails.badges.length > 0 &&
      experienceShopDetails.badges.includes(ExperienceBadge.FreeCancellation)
    );
  }
);

export const getExperiencesCheckoutBreakdown = createSelector(
  getExperiencesShopPriceBreakdown,
  getExperienceMaxTravelerCount,
  getIsFreeCancellation,
  (
    priceBreakdown,
    maxTravelerCount,
    isFreeCancellation
  ): IShopCheckoutBreakdown | null => {
    if (!priceBreakdown) {
      return null;
    }

    return {
      isSkeleton: false,
      priceBreakdown,
      experienceMaxTravelers: maxTravelerCount,
      isFreeCancellation,
    };
  }
);

export const getViewedExperienceDetailsProperties = createSelector(
  getExperiencesEntryProperties,
  getExperiencesShopDetails,
  (
    entryProperties,
    shopDetails
  ): ITrackingProperties<ViewedExperienceDetailsProperties> => ({
    properties: {
      ...entryProperties.properties,
      ...shopDetails?.trackingProperties.properties,
    },
    encryptedProperties: [
      ...entryProperties.encryptedProperties,
      shopDetails?.trackingProperties.encryptedProperties ?? "",
    ],
  })
);
